/**
 * La cuenta de un proveedor: cuánto le debo, por qué, y desde cuándo.
 *
 * ES UNA PANTALLA, NO UN MODAL
 * ----------------------------
 * Nació como diálogo y eso era un techo: un modal no aguanta extracto +
 * imputaciones + resumen mensual, rompe el botón de atrás del navegador y no se
 * puede compartir por link. `FichaCliente` ya lo tenía resuelto del otro lado, y
 * acá se sigue el mismo camino: `/proveedores?id=8` reemplaza la lista.
 *
 * CUATRO BLOQUES, EN ESTE ORDEN
 * -----------------------------
 * 1. EL SALDO, en las dos monedas por separado. Nunca sumadas: deber US$ 600 y
 *    $ 300.000 son dos deudas que se saldan por su lado, y juntarlas obligaría a
 *    elegir una cotización que nadie pactó.
 * 2. LO QUE QUEDA ABIERTO, con su antigüedad. Es lo que contesta «¿la factura 42
 *    se la pagué?», que es la pregunta con la que llama el proveedor.
 * 3. MES A MES, que contesta «¿viene creciendo?».
 * 4. EL EXTRACTO completo, del más viejo al más nuevo, donde cada compra enlaza
 *    a su remito y dice cómo quedó.
 *
 * UNA SOLA REQUEST DE DEUDAS. Se piden todas (`todas=1`) y de ahí salen los dos
 * usos: las abiertas para el bloque 2, y el conjunto entero para el estado de
 * cada renglón del extracto.
 */
import { useMemo, useState } from "react";
import { Banknote, Pencil } from "lucide-react";

import type { Moneda } from "@tora/shared";

import { aNumero } from "@/lib/tipos";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useCajas, useConfig, useCuentasFinancieras } from "@/lib/queries";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { Kpi } from "@/components/ui/kpi";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import type { CuentaFinanciera } from "@/components/cobro/logica";

import { DialogAjusteProveedor } from "./DialogAjusteProveedor";
import { DialogPagoProveedor } from "./DialogPagoProveedor";
import { ExtractoProveedor } from "./ExtractoProveedor";
import { ResumenPorMes } from "./ResumenPorMes";
import {
  useAjustarProveedor,
  useDeudasProveedor,
  useExtractoProveedor,
  useFichaProveedor,
  usePagarProveedor,
} from "./consultas";
import {
  antiguedadPorMoneda,
  haceCuanto,
  hayDeuda,
  nombreDeuda,
  type SaldoProveedor,
} from "./logica";

/** La misma tolerancia que el FIFO en la base. */
const CENTAVO = 0.009;

export interface FichaProveedorProps {
  proveedorId: number;
  /**
   * El diálogo de edición vive en la ruta, como en `FichaCliente`. Recibe el
   * proveedor de la ficha y su saldo: la ruta lo buscaba en su lista de
   * activos, y con uno dado de baja no lo encontraba y abría «Proveedor nuevo».
   */
  onEditar: (proveedor: SaldoProveedor, saldo: SaldoProveedor | null) => void;
}

export function FichaProveedor({ proveedorId, onEditar }: FichaProveedorProps): JSX.Element {
  const [pagando, setPagando] = useState(false);
  const [ajustando, setAjustando] = useState(false);

  const { data: ficha, isFetching, error } = useFichaProveedor(proveedorId);
  const { data: extracto, isFetching: cargandoExtracto } = useExtractoProveedor(proveedorId);
  const { data: deudas } = useDeudasProveedor(proveedorId, true);
  const { data: cuentasPagina } = useCuentasFinancieras();
  const { data: config } = useConfig();
  const { data: cajas } = useCajas();

  const pagar = usePagarProveedor(proveedorId);
  const ajustar = useAjustarProveedor(proveedorId);

  const cuentas = useMemo<CuentaFinanciera[]>(
    () => (cuentasPagina?.filas ?? []).filter((c) => c.activo !== false),
    [cuentasPagina],
  );

  const saldo = ficha?.saldo ?? null;
  const movimientos = useMemo(() => extracto?.filas ?? [], [extracto]);
  const todasLasDeudas = useMemo(() => deudas?.filas ?? [], [deudas]);
  const abiertas = useMemo(
    () => todasLasDeudas.filter((d) => aNumero(d.pendiente) > CENTAVO),
    [todasLasDeudas],
  );

  /**
   * La factura más vieja sin pagar, por moneda. Iba siempre bajo el KPI de
   * dólares aunque la deuda vieja fuera en pesos.
   */
  const antiguedad = useMemo(() => antiguedadPorMoneda(abiertas), [abiertas]);

  /** En qué monedas hubo movimientos. Una tabla del resumen por cada una. */
  const monedas = useMemo<Moneda[]>(() => {
    const vistas = new Set(movimientos.map((m) => m.moneda));
    return (["ARS", "USD"] as const).filter((m) => vistas.has(m));
  }, [movimientos]);

  if (error) {
    return (
      <EstadoVacio
        icono={Banknote}
        titulo="No pudimos abrir esa cuenta"
        detalle={error.mensaje ?? "El proveedor no existe."}
      />
    );
  }

  if (!ficha) {
    return (
      <Card padding="none">
        <Cargando texto={isFetching ? "Buscando…" : "Cargando la ficha…"} />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* 1 · El saldo. Una moneda por KPI, nunca sumadas. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <Kpi
            etiqueta="Le debo en dólares"
            monto={aNumero(saldo?.saldo_usd)}
            moneda="USD"
            tamano="lg"
            {...(antiguedad.USD !== null
              ? { nota: `la más vieja, ${haceCuanto(antiguedad.USD)}` }
              : {})}
          />
          <Kpi
            etiqueta="Le debo en pesos"
            monto={aNumero(saldo?.saldo_ars)}
            moneda="ARS"
            tamano="lg"
            {...(antiguedad.ARS !== null
              ? { nota: `la más vieja, ${haceCuanto(antiguedad.ARS)}` }
              : {})}
          />
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={() => setPagando(true)}
            disabled={saldo === null || !hayDeuda(saldo)}
          >
            <Banknote aria-hidden="true" />
            Pagarle
          </Button>
          <Button type="button" variant="outline" onClick={() => setAjustando(true)}>
            Ajustar
          </Button>
          <Button type="button" variant="outline" onClick={() => onEditar(ficha.proveedor, saldo)}>
            <Pencil aria-hidden="true" />
            Editar
          </Button>
        </div>
      </div>

      {/* 2 · Lo que queda abierto. Contesta "¿la 42 te la pagué?". */}
      {abiertas.length > 0 ? (
        <div className="space-y-2">
          <TituloSeccion>Lo que queda abierto</TituloSeccion>
          <Card padding="none" className="overflow-hidden">
            <Table densidad="compacta">
              <TableHeader>
                <TableRow>
                  <TableHead>Qué</TableHead>
                  <TableHead>Desde</TableHead>
                  <TableHead className="text-right">Pendiente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {abiertas.map((d) => (
                  <TableRow key={d.movimiento_id}>
                    <TableCell className="text-sm">{nombreDeuda(d)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {haceCuanto(d.dias)}
                    </TableCell>
                    <TableCellImporte monto={aNumero(d.pendiente)} moneda={d.moneda} tamano="sm" />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      ) : null}

      {/* 3 · Cómo fluctuó. */}
      <ResumenPorMes movimientos={movimientos} monedas={monedas} />

      {/* 4 · De dónde viene cada peso. */}
      <ExtractoProveedor
        movimientos={movimientos}
        deudas={todasLasDeudas}
        cargando={cargandoExtracto}
      />

      <DialogPagoProveedor
        proveedor={pagando ? saldo : null}
        deudas={abiertas}
        cuentas={cuentas}
        cotizacion={aNumero(config?.cotizacion_vigente) || 1}
        cajas={cajas ?? []}
        guardando={pagar.isPending}
        onCerrar={() => setPagando(false)}
        onConfirmar={(datos) =>
          pagar.mutate(datos, {
            onSuccess: () => {
              setPagando(false);
              mostrarExito("Pago registrado");
            },
            onError: mostrarError,
          })
        }
      />

      <DialogAjusteProveedor
        abierto={ajustando}
        nombre={ficha.proveedor.nombre}
        guardando={ajustar.isPending}
        onCerrar={() => setAjustando(false)}
        onConfirmar={(datos) =>
          ajustar.mutate(datos, {
            onSuccess: () => {
              setAjustando(false);
              mostrarExito("Cuenta ajustada");
            },
            onError: mostrarError,
          })
        }
      />
    </div>
  );
}
